<template>
	<AppModal @hide-modal="closeModal">
		<template #header>
			<div class="extraction-modal-header">
				<span>{{ suggestion.documentName ?? "Onbekend document" }}</span>
				<span class="extraction-modal-label">{{
					suggestion.classificationLabel
				}}</span>
			</div>
		</template>

		<!-- Loading -->
		<AppModalContent v-if="loading">
			<div class="extraction-modal-loading">
				<span class="ht-icon spin">progress_activity</span>
				<span>Geëxtraheerde gegevens ophalen…</span>
			</div>
		</AppModalContent>

		<!-- Error -->
		<AppModalContent v-else-if="error">
			<div class="extraction-modal-error">
				<span class="ht-icon" style="color: var(--color-fail)">error</span>
				<span>{{ error }}</span>
			</div>
		</AppModalContent>

		<!-- Unmatched: geen customer gekoppeld (dead-end alleen als er géén
		     klantkeuze mogelijk is; bij heractivering tonen we de keuze in de
		     diff-branch hieronder) -->
		<AppModalContent
			v-else-if="
				suggestion.matchedCustomerId === null &&
				!customerId &&
				!needsCustomerChoice
			"
		>
			<div class="extraction-modal-info">
				<span class="ht-icon">person_off</span>
				<span
					>Koppel dit document eerst aan een klant om gegevens te kunnen
					vergelijken.</span
				>
			</div>
		</AppModalContent>

		<!-- Onbekend doctype -->
		<AppModalContent v-else-if="!config">
			<div class="extraction-modal-info">
				<span class="ht-icon">info</span>
				<div>
					<p class="extraction-modal-info-title">
						Vergelijking nog niet beschikbaar
					</p>
					<p class="extraction-modal-info-sub">
						Voor dit documenttype is de veldmapping nog niet geconfigureerd.
					</p>
				</div>
			</div>
			<div v-if="extractionData" class="extraction-modal-raw">
				<p class="extraction-modal-raw-title">Geëxtraheerde waarden:</p>
				<div
					v-for="(value, key) in visibleRawFields"
					:key="String(key)"
					class="extraction-modal-raw-row"
				>
					<span class="extraction-modal-raw-key">{{ key }}</span>
					<span class="extraction-modal-raw-value">{{ value }}</span>
				</div>
			</div>
		</AppModalContent>

		<!-- Normaal: diff tabel -->
		<AppModalContent v-else>
			<!-- Confidence waarschuwing -->
			<div
				v-if="suggestion.matchConfidence < 0.8"
				class="extraction-modal-warning"
			>
				<span class="ht-icon">warning</span>
				<span
					>Koppeling onzeker — controleer of dit document bij de juiste klant
					hoort.</span
				>
			</div>

			<!-- SPEC-112: klantkeuze bij heractiveerde identity-suggestie -->
			<div v-if="needsCustomerChoice" class="extraction-modal-destination">
				<label class="extraction-modal-destination-label">Klant:</label>
				<select v-model="selectedCustomerId" class="extraction-modal-select">
					<option :value="null" disabled>Kies een klant…</option>
					<option
						v-for="customer in customers"
						:key="customer.id"
						:value="customer.id"
					>
						{{ customerLabel(customer) }}
					</option>
				</select>
			</div>

			<!-- Bestemmingskeuze bij income -->
			<div
				v-if="config.resolverType === 'incomeEntry' && effectiveCustomerId"
				class="extraction-modal-destination"
			>
				<label class="extraction-modal-destination-label">Loondienst:</label>
				<select
					v-model="selectedEntryId"
					aria-label="Loondienst"
					class="extraction-modal-select"
				>
					<option :value="null" disabled>Kies een loondienst</option>
					<option
						v-for="entry in incomeEntries"
						:key="entry.id"
						:value="entry.id"
					>
						{{ entry.label }}
					</option>
					<option value="__new__">+ Nieuwe loondienst aanmaken</option>
				</select>
			</div>

			<!-- Diff tabel -->
			<div v-if="diffRows.length > 0" class="extraction-diff-table">
				<div class="extraction-diff-header">
					<span class="extraction-diff-col-check"></span>
					<span class="extraction-diff-col-label">Veld</span>
					<span class="extraction-diff-col-current">Huidig</span>
					<span class="extraction-diff-col-new">Geëxtraheerd</span>
					<span class="extraction-diff-col-conf"></span>
				</div>
				<div
					v-for="row in diffRows"
					:key="row.extractionKey"
					class="extraction-diff-row"
					:class="{
						'extraction-diff-row--info': row.isInfoOnly,
						'extraction-diff-row--conflict': row.hasConflict,
					}"
				>
					<span class="extraction-diff-col-check">
						<input
							v-if="!row.isInfoOnly"
							v-model="checkedFields[row.extractionKey]"
							type="checkbox"
						/>
						<span v-else class="ht-icon extraction-diff-info-icon">info</span>
					</span>
					<span class="extraction-diff-col-label">{{ row.label }}</span>
					<span class="extraction-diff-col-current">
						<template v-if="row.isInfoOnly">—</template>
						<template v-else-if="isEmpty(row.currentValue)"
							><span class="extraction-diff-empty">leeg</span></template
						>
						<template v-else>{{ row.currentValue }}</template>
					</span>
					<span class="extraction-diff-col-new">
						{{ row.extractedValue }}
						<span
							v-if="row.hasConflict && row.isRiskField"
							class="extraction-diff-risk"
							>⚠ risico</span
						>
					</span>
					<span class="extraction-diff-col-conf">
						<span
							v-if="row.confidence != null"
							class="extraction-diff-confidence"
							:class="confidenceClass(row.confidence)"
							:title="`${Math.round(row.confidence * 100)}% confidence`"
						>
							●
						</span>
					</span>
				</div>
			</div>

			<div v-else class="extraction-modal-info">
				<span class="ht-icon">check_circle</span>
				<span>Alle geëxtraheerde waarden komen overeen met het dossier.</span>
			</div>
		</AppModalContent>

		<AppModalContent v-if="staleConflicts.length > 0">
			<div class="extraction-modal-warning">
				<span class="ht-icon">warning</span>
				<span
					>Een of meer velden zijn ondertussen gewijzigd. Herlaad de
					vergelijking.</span
				>
			</div>
		</AppModalContent>

		<AppModalContent v-if="applyError">
			<div class="extraction-modal-error">
				<span class="ht-icon" style="color: var(--color-fail)">error</span>
				<span>{{ applyError }}</span>
			</div>
		</AppModalContent>

		<template #footer>
			<p
				v-if="!applied && blockedReason"
				class="px-6 text-text-secondary"
				role="status"
			>
				{{ blockedReason }}
			</p>
			<AppButtonGroup>
				<AppButton v-if="error" variant="primary" @click="fetchExtraction">
					Opnieuw proberen
				</AppButton>
				<AppButton v-if="applied" variant="primary" disabled>
					Toegepast
				</AppButton>
				<AppButton
					v-else
					variant="primary"
					:loading="applying"
					:disabled="Boolean(blockedReason)"
					@click="applyExtraction"
				>
					Toepassen ({{ checkedCount }})
				</AppButton>
				<AppButton variant="secondary" @click="closeModal">
					Annuleren
				</AppButton>
			</AppButtonGroup>
		</template>
	</AppModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { api } from "@/loanLinkApi";
import { ContentType } from "@/generatedApi";
import type { ExtractionSuggestion } from "@/composables/useExtractionSuggestions";
import type { InventarisationCustomer } from "@loanlink-nl/model-app";
import { extractionFieldConfigs } from "@/helpers/inventarisation/extractionFieldConfigs";
import {
	calculateDiff,
	resolvers,
	type DiffRow,
} from "@/helpers/inventarisation/extractionDiff";

interface IncomeEntry {
	id: string;
	label: string;
}

const props = withDefaults(
	defineProps<{
		suggestion: ExtractionSuggestion;
		dossierId: string;
		customerId: string | null;
		versionData: Record<string, unknown> | null;
		incomeEntries?: IncomeEntry[];
		customers?: InventarisationCustomer[];
	}>(),
	{
		incomeEntries: () => [],
		customers: () => [],
	},
);

const emit = defineEmits<{
	close: [];
	markBekeken: [suggestionId: string];
	applied: [
		data: {
			auditId: string;
			fieldsApplied: number;
			createdEntryId: string | null;
		},
	];
}>();

const loading = ref(false);
const error = ref<string | null>(null);
const applying = ref(false);
const applied = ref(false);
const applyError = ref<string | null>(null);
const staleConflicts = ref<
	Array<{ extractionKey: string; expected: unknown; actual: unknown }>
>([]);
const extractionData = ref<Record<string, unknown> | null>(null);
let contextActive = true;
let fetchGeneration = 0;
function invalidateContext() {
	contextActive = false;
	fetchGeneration++;
}
function closeModal() {
	invalidateContext();
	emit("close");
}
onBeforeUnmount(invalidateContext);
watch(
	() => [
		props.dossierId,
		props.customerId,
		props.suggestion.id,
		props.suggestion.documentId,
	],
	closeModal,
	{ flush: "sync" },
);

const selectedEntryId = ref<string | null>(null);
const checkedFields = ref<Record<string, boolean>>({});

// SPEC-112: bij een heractiveerde identity-suggestie (doel-klant verwijderd) is
// props.customerId null. De adviseur kiest hier expliciet een klant; die keuze
// voedt vervolgens zowel de diff-resolver als de POST-payload.
const selectedCustomerId = ref<string | null>(null);

const needsCustomerChoice = computed(
	() =>
		props.suggestion.reactivatedAfterDelete === true &&
		props.suggestion.matchedCustomerId === null &&
		!props.customerId,
);

// De klant die de resolver en POST-payload moeten gebruiken.
const effectiveCustomerId = computed(
	() => selectedCustomerId.value ?? props.customerId,
);

function customerLabel(customer: InventarisationCustomer): string {
	const first = customer.personDetails?.firstName ?? "";
	const last = customer.personDetails?.lastName ?? "";
	const name = `${first} ${last}`.trim();
	return name || customer.id;
}

const config = computed(
	() => extractionFieldConfigs[props.suggestion.classificationLabel] ?? null,
);

const visibleRawFields = computed(() => {
	if (!extractionData.value) return {};
	const result: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(extractionData.value)) {
		// Verberg _confidence keys
		if (key.endsWith("_confidence")) continue;
		result[key] = value;
	}
	return result;
});

const diffRows = computed<DiffRow[]>(() => {
	if (!extractionData.value || !config.value || !props.versionData) return [];

	const resolver = resolvers[config.value.resolverType];
	if (!resolver) return [];

	const entryId =
		selectedEntryId.value === "__new__" ? null : selectedEntryId.value;
	const currentValues = resolver(
		props.versionData,
		effectiveCustomerId.value,
		entryId,
	);

	return calculateDiff({
		extractionFields: extractionData.value,
		currentValues: selectedEntryId.value === "__new__" ? null : currentValues,
		config: config.value,
	});
});

// Initialiseer checkbox defaults wanneer diffRows wijzigen
watch(
	diffRows,
	(rows) => {
		const newChecked: Record<string, boolean> = {};
		for (const row of rows) {
			if (!row.isInfoOnly) {
				newChecked[row.extractionKey] = row.defaultChecked;
			}
		}
		checkedFields.value = newChecked;
	},
	{ immediate: true },
);

// Default bestemming: zet eerste entry direct, verfijn bij werkgever-match
const userSelectedEntry = ref(false);

// Zet eerste entry als default zodra entries beschikbaar
watch(
	() => props.incomeEntries,
	(entries) => {
		if (!entries?.length) return;
		if (selectedEntryId.value != null) return;
		selectedEntryId.value = entries[0]?.id ?? null;
	},
	{ immediate: true },
);

// Na extractionData laden: probeer werkgever-match
watch(extractionData, () => {
	if (!extractionData.value) return;
	if (userSelectedEntry.value) return;
	const entries = props.incomeEntries;
	if (!entries?.length) return;

	const extractedEmployer = extractionData.value.Werkgever;
	if (extractedEmployer && typeof extractedEmployer === "string") {
		const match = entries.find((e) =>
			e.label.toLowerCase().includes(extractedEmployer.toLowerCase()),
		);
		if (match) {
			selectedEntryId.value = match.id;
		}
	}
});

// Track handmatige selectie
watch(selectedEntryId, (val, oldVal) => {
	if (oldVal != null && val !== oldVal) {
		userSelectedEntry.value = true;
	}
});

async function fetchExtraction() {
	if (!contextActive) return;
	const generation = ++fetchGeneration;
	loading.value = true;
	error.value = null;

	try {
		const label = encodeURIComponent(props.suggestion.classificationLabel);
		const { data } = await api.request<{
			extractionResult: Record<string, unknown>;
		}>({
			path: `/document/${props.suggestion.documentId}/extraction?label=${label}`,
			method: "GET",
			secure: true,
			format: "json",
		});
		if (!contextActive || generation !== fetchGeneration) return;
		// Backend retourneert { fields: {...}, status: "..." } — normaliseer naar platte velden
		const result = data.extractionResult;
		extractionData.value =
			(result?.fields as Record<string, unknown>) ?? result;
		emit("markBekeken", props.suggestion.id);
	} catch {
		if (contextActive && generation === fetchGeneration)
			error.value = "Kon de geëxtraheerde gegevens niet ophalen.";
	} finally {
		if (contextActive && generation === fetchGeneration) loading.value = false;
	}
}

function confidenceClass(confidence: number): string {
	if (confidence < 0.5) return "confidence-low";
	if (confidence < 0.85) return "confidence-medium";
	return "confidence-high";
}

function isEmpty(value: unknown): boolean {
	return value == null || value === "";
}

const checkedCount = computed(
	() => Object.values(checkedFields.value).filter(Boolean).length,
);

const blockedReason = computed(() => {
	if (!contextActive) return "Deze selectie is niet meer actief.";
	if (loading.value) return "Gegevens worden opgehaald.";
	if (error.value) return "Haal de gegevens opnieuw op om toe te passen.";
	if (applying.value) return "Gegevens worden toegepast.";
	if (applied.value) return "Deze gegevens zijn al toegepast.";
	if (!config.value)
		return "Voor dit documenttype is toepassen nog niet beschikbaar.";
	if (!effectiveCustomerId.value) return "Kies eerst een klant.";
	if (config.value.resolverType === "incomeEntry") {
		const customers = props.versionData?.customers as
			| Array<{ id: string }>
			| undefined;
		if (
			!customers?.some((c) => c.id === effectiveCustomerId.value) ||
			(props.suggestion.matchedCustomerId !== null &&
				props.suggestion.matchedCustomerId !== effectiveCustomerId.value)
		)
			return "Deze extractie hoort niet bij de gekozen klant.";
		if (
			selectedEntryId.value !== "__new__" &&
			(!selectedEntryId.value ||
				!props.incomeEntries?.some(
					(entry) => entry.id === selectedEntryId.value,
				) ||
				!resolvers.incomeEntry?.(
					props.versionData!,
					effectiveCustomerId.value,
					selectedEntryId.value,
				))
		)
			return "Kies een bestaande loondienst of maak bewust een nieuwe aan.";
	}
	if (diffRows.value.length === 0)
		return "Er zijn geen verschillen om toe te passen.";
	if (checkedCount.value === 0)
		return "Selecteer de gegevens die u wilt toepassen.";
	return "";
});

async function applyExtraction() {
	if (!contextActive || blockedReason.value) return;
	applying.value = true;
	applyError.value = null;
	staleConflicts.value = [];

	try {
		const { data } = await api.request<{
			auditId: string;
			fieldsApplied: number;
			createdEntryId: string | null;
		}>({
			path: `/dossier/${props.dossierId}/apply-extraction`,
			method: "POST",
			secure: true,
			type: ContentType.Json,
			format: "json",
			body: {
				suggestionId: props.suggestion.id,
				customerId: effectiveCustomerId.value,
				targetEntryId:
					selectedEntryId.value === "__new__" ? null : selectedEntryId.value,
				createNewEntry: selectedEntryId.value === "__new__",
				fieldsToApply: diffRows.value
					.filter((r) => !r.isInfoOnly && checkedFields.value[r.extractionKey])
					.map((r) => ({
						extractionKey: r.extractionKey,
						expectedOldValue: r.currentValue ?? null,
					})),
			},
		});

		if (!contextActive) return;
		applied.value = true;
		emit("applied", data);
	} catch (err: unknown) {
		if (!contextActive) return;
		const apiErr = err as {
			status?: number;
			error?: {
				error?: string;
				conflicts?: Array<{
					extractionKey: string;
					expected: unknown;
					actual: unknown;
				}>;
			};
		};
		if (apiErr.status === 409 && apiErr.error?.conflicts) {
			staleConflicts.value = apiErr.error.conflicts;
		} else {
			applyError.value = "Toepassen mislukt.";
		}
	} finally {
		if (contextActive) applying.value = false;
	}
}

onMounted(fetchExtraction);
</script>

<style scoped>
.extraction-modal-header {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.extraction-modal-label {
	font-size: 0.65rem;
	padding: 0.1rem 0.35rem;
	background: var(--accent-primary, #7c3aed);
	color: white;
	border-radius: 0.2rem;
	font-weight: 400;
}

.extraction-modal-loading,
.extraction-modal-error,
.extraction-modal-info {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 1rem 0;
	color: var(--text-muted, #6b7280);
}

.extraction-modal-info-title {
	font-weight: 600;
	color: var(--text-primary, #111827);
	margin-bottom: 0.25rem;
}

.extraction-modal-info-sub {
	font-size: 0.8rem;
}

.extraction-modal-warning {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 0.75rem;
	background: var(--color-warning-bg, #fef3c7);
	border-radius: 0.375rem;
	margin-bottom: 0.75rem;
	font-size: 0.8rem;
	color: var(--color-warning-text, #92400e);
}

.extraction-modal-destination {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 0.75rem;
}

.extraction-modal-destination-label {
	font-size: 0.8rem;
	font-weight: 500;
	color: var(--text-primary, #111827);
}

.extraction-modal-select {
	font-size: 0.8rem;
	padding: 0.25rem 0.5rem;
	border: 1px solid var(--border, #e5e7eb);
	border-radius: 0.25rem;
	flex: 1;
}

.extraction-diff-table {
	border: 1px solid var(--border, #e5e7eb);
	border-radius: 0.375rem;
	overflow: hidden;
}

.extraction-diff-header,
.extraction-diff-row {
	display: grid;
	grid-template-columns: 2rem 1fr 1fr 1fr 1.5rem;
	gap: 0.5rem;
	padding: 0.5rem 0.75rem;
	align-items: center;
	font-size: 0.8rem;
}

.extraction-diff-header {
	background: var(--background-secondary, #f9fafb);
	font-weight: 600;
	font-size: 0.7rem;
	color: var(--text-muted, #6b7280);
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.extraction-diff-row {
	border-top: 1px solid var(--border, #e5e7eb);
}

.extraction-diff-row--info {
	background: var(--background-secondary, #f9fafb);
	opacity: 0.8;
}

.extraction-diff-row--conflict {
	background: var(--color-fail-bg, #fef2f2);
}

.extraction-diff-info-icon {
	font-size: 0.9rem;
	color: var(--text-muted, #6b7280);
}

.extraction-diff-empty {
	color: var(--text-muted, #6b7280);
	font-style: italic;
}

.extraction-diff-risk {
	font-size: 0.7rem;
	color: var(--color-fail, #dc2626);
	margin-left: 0.25rem;
}

.extraction-diff-confidence {
	font-size: 0.7rem;
}

.confidence-high {
	color: var(--color-success, #16a34a);
}

.confidence-medium {
	color: var(--color-warning, #ca8a04);
}

.confidence-low {
	color: var(--color-fail, #dc2626);
}

.extraction-modal-raw {
	margin-top: 0.75rem;
	border: 1px solid var(--border, #e5e7eb);
	border-radius: 0.375rem;
	overflow: hidden;
}

.extraction-modal-raw-title {
	font-size: 0.75rem;
	font-weight: 600;
	padding: 0.5rem 0.75rem;
	background: var(--background-secondary, #f9fafb);
	color: var(--text-muted, #6b7280);
}

.extraction-modal-raw-row {
	display: flex;
	padding: 0.35rem 0.75rem;
	border-top: 1px solid var(--border, #e5e7eb);
	font-size: 0.8rem;
}

.extraction-modal-raw-key {
	width: 40%;
	color: var(--text-muted, #6b7280);
}

.extraction-modal-raw-value {
	flex: 1;
	color: var(--text-primary, #111827);
}

.spin {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}
</style>
